import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useAppSelector } from "../../redux";
import { formatTime } from "../../utils/utils";

const margin = { top: 0, right: 35, bottom: 80, left: 103 },
  width = 1000,
  height = 400;

const BuildingLinechart = ({ buildingName, upperDomain }) => {
  const parseTime = d3.timeParse("%Y-%m-%dT%H:%M"); // create function to parse string to time object
  const lastUpdated = useAppSelector((state) => state.steam.lastUpdated);
  const allData = useAppSelector((state) => state.steamKPI).data;
  const averageEnergy = d3.mean(allData, (d) => d.value);

  // import data and make into one array
  const graphData = useAppSelector((state) => state.steamKPI).data.filter(
    (item) => item.buildingName === buildingName
  );

  console.log(graphData);
  const ref = useRef();

  // Specify the chart's dimensions.
  useEffect(() => {
    if (graphData.length === 0) {
      const retryTimeout = setTimeout(() => {
        // Call the useEffect again after 10 seconds
        clearTimeout(retryTimeout);
      }, 10000); // 10 seconds

      return; // Exit early without rendering the chart
    }

    // Clear old data
    d3.select(ref.current).selectAll("*").remove();

    // Create the SVG container.
    const svg = d3
      .select(ref.current)
      .append("g")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .attr(
        "style",
        "max-width: 100%; height: auto; overflow: visible; font: 10px National2;"
      );

    // Select and remove old axis elements
    svg.selectAll(".axis").remove();

    const x = d3
      .scaleUtc()
      .domain(d3.extent(graphData, (d) => parseTime(d.timestamp)))
      .range([margin.left, width - margin.right]);

    console.log(x.domain());
    // Define the time format for the ticks on the x-axis
    const timeFormat = d3.timeFormat("%B %e %I:%M %p");

    const y = d3
      .scaleLinear()
      .domain([0, upperDomain])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const xAxis = d3
      .axisBottom(x)
      .ticks(width / 300)
      .tickSizeOuter(0)
      .tickFormat(d3.timeFormat("%Y"));
    // Add the horizontal axis
    svg
      .append("g")
      .attr("transform", `translate(-20,${height - margin.bottom + 10})`)
      .attr("class", "x-axis")
      .call(xAxis)
      .selectAll("text")
      .style("font-size", "10px");
    const yAxis = d3.axisLeft(y).ticks(5).tickFormat(d3.format(".2s"));

    // Add the vertical axis.
    svg
      .append("g")
      .attr("transform", `translate(${margin.left - 20},10)`)
      .call(yAxis)
      .call((g) => g.selectAll(".tick line").style("stroke", "#888888"))
      .call(
        (g) => g.selectAll("text").attr("fill", "grey") // Set y-axis tick label color to grey
      )
      .call((g) =>
        g
          .append("text")
          .attr("x", -margin.left + 70)
          .attr("y", -20)
          .attr("fill", "black")
          .attr("text-anchor", "start")
          .text("Energy Consumption (KwH)")
          .style("font-family", "National2")
          .style("font-size", "15px")
      )
      .style("font-size", "15px")
      .style("font-family", "National2")
      .selectAll(".tick line, .domain")
      .style("stroke", "black");

    // Compute the points in pixel space as [x, y, curValue].
    const points = graphData
      .map((d) => [x(parseTime(d.timestamp)), y(d.value), d.value])
      .filter((point) => point.every((coord) => coord !== undefined));

    // Draw the line.
    const line = d3
      .line()
      .x((d) => x(parseTime(d.timestamp)))
      .y((d) => y(d.value));

    const path = svg
      .append("path")
      .datum(graphData)
      .attr("fill", "none")
      .attr("stroke", "#FFA00F")
      .attr("stroke-width", 5)
      .attr("d", line);

    // Add circles for each data point
    svg
      .append("g")
      .attr("transform", `translate(-20,${height - margin.bottom + 10})`)
      .call(xAxis)
      .selectAll(".tick")
      .each(function (d) {
        const tick = d3.select(this);
        const xPos = tick
          .attr("transform")
          .replace("translate(", "")
          .split(",")[0];
        graphData.forEach((data) => {
          if (parseTime(data.timestamp).getTime() === d.getTime()) {
            svg
              .append("circle")
              .attr("cx", xPos)
              .attr("cy", y(data.value))
              .attr("r", 10)
              .attr("fill", "#FFA00F");
          }
        });
      });

    svg
      .append("line")
      .attr("x1", margin.left)
      .attr("x2", width - margin.right)
      .attr("y1", y(averageEnergy))
      .attr("y2", y(averageEnergy))
      .attr("stroke", "#FFA00F")
      .attr("stroke-width", "2")
      .attr("stroke-dasharray", "5,5")
      .attr("transform", "translate(-20,10)");

    svg
      .append("text")
      .attr("x", width - margin.right)
      .attr("y", y(averageEnergy) - 5)
      .attr("font-family", "National2")
      .attr("font-size", "15px")
      .attr("text-anchor", "end")
      .text("Campus Average");

    // Add an invisible layer for the interactive tip.
    const dot = svg.append("g").attr("display", "none");

    dot.append("circle").attr("r", 2.5);

    dot.append("text").attr("text-anchor", "middle").attr("y", -8);

    svg
      .on("pointerenter", pointerentered)
      .on("pointermove", pointermoved)
      .on("pointerleave", pointerleft)
      .on("touchstart", (event) => event.preventDefault());

    // When the pointer moves, find the closest point, update the interactive tip, and highlight
    // the corresponding line.
    function pointermoved(event) {
      const [xm, ym] = d3.pointer(event);
      const i = d3.leastIndex(points, ([x, y]) => Math.hypot(x - xm, y - ym));
      const [x, y, value] = points[i];
      path.style("stroke", "#ddd").raise();
      dot.attr("transform", `translate(${x},${y})`);
      dot
        .select("text")
        .text(`${value} kWh`)
        .attr("font-family", "National2") // Change font
        .attr("font-size", "15px"); // Change font size
      svg.property("value", graphData[i]).dispatch("input", { bubbles: true });
    }

    function pointerentered() {
      path.style("mix-blend-mode", null).style("stroke", "#ddd");
      dot.attr("display", null);
    }

    function pointerleft() {
      path.style("mix-blend-mode", "multiply").style("stroke", null);
      dot.attr("display", "none");
      svg.node().value = null;
      svg.dispatch("input", { bubbles: true });
    }

    svg
      .append("text")
      .attr("class", "last-updated")
      .attr("x", 300)
      .attr("y", height - margin.bottom + 80)
      .attr("text-anchor", "end")
      .attr("font-size", "14px")
      .style("font-style", "italic")
      .attr("font-family", "National2")
      .text("Last Updated: " + formatTime(lastUpdated)); // Use timestamp from data parsed with parseTime
  }, [graphData, averageEnergy, upperDomain, lastUpdated, parseTime]);

  return (
    <>
      <svg
        width={width}
        height={height}
        id={`${buildingName}linechart`}
        ref={ref}
      />
    </>
  );
};

export default BuildingLinechart;
